class AvCartRecs extends HTMLElement {
  #request;
  #observer;

  async connectedCallback() {
    let request;
    try {
      this.#observer?.disconnect();
      this.#request?.abort();
      request = this.#request = new AbortController();
      this.hidden = true;
      const container = this.querySelector('.av-cart-recs__list');
      if (!container || !this.dataset.productId) return;
      container.replaceChildren();
      const { productId, rootUrl, cartIds: cartIdsValue } = this.dataset;
      const cartIds = new Set(cartIdsValue.split(','));
      const notInCart = (product) => !cartIds.has(String(product.id)) && product.available !== false && product.variants?.[0];

      // Complementary is tried first because those pairings are curated by the
      // merchant in Search and Discovery: the belt for the dress. intent=related
      // is a guess from order history, and this store has no order history yet,
      // so related stays only as the fallback that keeps the row populated
      // until complementary has been curated for a product.
      const fetchRecommendations = async (intent) => {
        const url = new URL(`${rootUrl.replace(/\/?$/, '/')}recommendations/products.json`, location.origin);
        url.search = new URLSearchParams({ product_id: productId, limit: '8', intent });
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        return (await response.json()).products;
      };
      const complementary = (await fetchRecommendations('complementary')).filter(notInCart);
      const related = complementary.length < 3 ? (await fetchRecommendations('related')).filter(notInCart) : [];
      if (request.signal.aborted || !this.isConnected) return;
      if (productId !== this.dataset.productId || cartIdsValue !== this.dataset.cartIds) {
        return this.connectedCallback();
      }
      // Merged list keeps complementary first, drops any id that appears in
      // both responses, and never exceeds the per-request limit of 8.
      const seen = new Set();
      const products = [...complementary, ...related]
        .filter((product) => {
          if (seen.has(product.id)) return false;
          seen.add(product.id);
          return true;
        })
        .slice(0, 8)
        .slice(0, 4);
      if (request.signal.aborted || !this.isConnected || products.length < 2) return;

      // Build the complete subtree before connecting it so the theme can resolve its refs.
      const cards = products.map((product) => this.#createCard(product));
      container.replaceChildren(...cards);
      this.hidden = false;
    } catch {
      if (this.#request === request) this.hidden = true;
    } finally {
      try {
        if (this.isConnected && this.#request === request) {
          // Horizon can morph an existing element instead of reconnecting it.
          this.#observer ??= new MutationObserver((records) => {
            if (records.some((record) => record.type === 'attributes') ||
                !this.querySelector('.av-cart-recs__list')?.children.length) {
              this.connectedCallback();
            }
          });
          this.#observer.observe(this, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-product-id', 'data-cart-ids'],
          });
        }
      } catch {
        this.hidden = true;
      }
    }
  }

  disconnectedCallback() {
    try {
      this.#observer?.disconnect();
      this.#request?.abort();
    } catch {
      this.hidden = true;
    }
  }

  #createCard(product) {
    const card = document.createElement('li');
    card.className = 'av-cart-recs__item';
    // Only static markup is interpolated; product content is assigned as text or attributes.
    card.innerHTML = `
      <a class="av-cart-recs__media" tabindex="-1" aria-hidden="true"></a>
      <a class="av-addons__title"></a>
      <p class="av-cart-recs__price"></p>
      <product-form-component class="av-addons__form" data-section-id="cart-drawer-section"
        on:submit="/handleSubmit" data-quantity-default="1">
        <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
        <form method="post" data-type="add-to-cart-form" accept-charset="UTF-8">
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="id" ref="variantId">
          <input type="hidden" name="quantity" value="1">
          <add-to-cart-component ref="addToCartButtonContainer" data-add-to-cart-animation="false">
            <button type="submit" name="add" ref="addToCartButton" on:click="/handleClick"
              class="button button-secondary av-addons__add"><span class="quick-add__button-label">Add</span></button>
          </add-to-cart-component>
        </form>
      </product-form-component>`;
    const productUrl = new URL(product.url, location.origin);
    if (!['http:', 'https:'].includes(productUrl.protocol)) throw new Error('Invalid product URL');
    card.querySelectorAll('a').forEach((link) => { link.href = productUrl.href; });
    card.querySelector('.av-addons__title').textContent = product.title;
    card.querySelector('.av-cart-recs__price').textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(Number(product.price) / 100);

    const imageSource = product.featured_image || product.images?.[0];
    if (imageSource) {
      const imageUrl = new URL(imageSource, location.origin);
      if (!['http:', 'https:'].includes(imageUrl.protocol)) throw new Error('Invalid image URL');
      imageUrl.searchParams.set('width', '300');
      const image = document.createElement('img');
      image.src = imageUrl.href;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 300;
      image.height = 400;
      image.className = 'av-cart-recs__image';
      card.querySelector('.av-cart-recs__media').append(image);
    }

    const component = card.querySelector('product-form-component');
    component.id = `AvCartRecs-${product.id}`;
    component.dataset.productId = String(product.id);
    component.dataset.productUrl = productUrl.href;
    const form = card.querySelector('form');
    form.id = `AvCartRecsForm-${product.id}`;
    form.action = this.dataset.cartAddUrl;
    form.querySelector('[name="id"]').value = product.variants[0].id;
    return card;
  }
}

try {
  if (!customElements.get('av-cart-recs')) customElements.define('av-cart-recs', AvCartRecs);
} catch {
  // Recommendations must never prevent the cart drawer from working.
}
